/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React from 'react';

import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';

import { Locale, LocaleKeys } from '../../../core/locale';
import { TiVoMemo } from '../../../core/tivo/tivo-react-wrapper';
import { DateTime } from '../../../core/utils';
import { Mousetrap } from '../../common';
import { RecordingStatusIcon } from '../../Guide/RecordingStatusIcon';

const UpcomingListItem: React.FC<{
    item: UpcomingOffer | UpcomingOfferV2 | any;
    index: number;
    isSelected?: boolean;
    ghostMode?: boolean;
    newIconUrl?: string;
}> = ({ item, index, isSelected, ghostMode, newIconUrl }) => {
    const formatDate = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%M/%D') : '';
    };

    const formatTime = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%l:%i%p') : '';
    };

    const getResolutionIcon = (resolution: string): string => {
        switch (resolution.toLowerCase()) {
            case 'hd':
                return 'HD';
            case 'sd':
                return 'SD';
            case '4k':
            case 'uhd':
                return '4K';
            default:
                return '';
        }
    };

    const formatEpisodeTitle = (offer: UpcomingOffer | UpcomingOfferV2): string => {
        const { episodeTitle, episodeNumber, seasonNumber, title } = offer as any;

        if (seasonNumber && episodeNumber) {
            return `S${seasonNumber} E${episodeNumber} '${episodeTitle || title}'`;
        }
        if (seasonNumber) {
            return `S${seasonNumber} '${episodeTitle || title}'`;
        }

        return episodeTitle || title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    return (
        <Mousetrap
            value={`${index}_list-view`}
            className={`upcoming-list-item ${isSelected ? ` focused${ghostMode ? ' ghostHighlight' : ''}` : ''}`}
        >
            <div className="upcoming-list-item-content">
                <div className="item-date">{formatDate(item.startTime)}</div>

                <div className="item-time">{formatTime(item.startTime)}</div>

                <div className="item-content">
                    <span className="item-title">{formatEpisodeTitle(item)}</span>
                    {item.isNew && (
                        <img
                            src={newIconUrl}
                            className="icon-new"
                            data-testid={'program-cell-new-icon'}
                        />
                    )}
                </div>

                <div className="item-channel-info">
                    <span className="channel-number">{item.channel.channelNumber}</span>
                    {getResolutionIcon(item.videoResolution || '') && (
                        <span className="resolution-badge">
                            {item.videoResolution && (
                                <span>{getResolutionIcon(item.videoResolution || '')}</span>
                            )}
                        </span>
                    )}
                    <div className="additional-icons">
                        {item.recordingStatus && (
                            <RecordingStatusIcon type={item.recordingStatus} />
                        )}
                    </div>
                </div>
            </div>
        </Mousetrap>
    );
};

export default TiVoMemo(UpcomingListItem);
